import { RetryLimitUnit } from "@common/types/interface";
import React, { memo, useState } from "react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { X, Settings } from "lucide-react";
import { MessageOvedueLimit } from '@/bt-common/services/utilsCommon';
import { useAppDispatch, useAppSelector } from "@common/reducers/hooks/store.hook";
import { setRetryLimitDuration, setRetryLimitUnit } from "@common/reducers/ScheduledMessageEditorReducer";
import { cn } from "@/lib/utils";
import { useTranslation } from 'react-i18next';
import { namespace } from '@common/config/constants';
import type { CommonRootState } from '@common/types/common-root-state-type';
import { effectiveRetryWindow, normalizeRetryDuration } from './retry-duration';

const DefaultRetryLimitUnit: RetryLimitUnit = 'minutes';

type MessageDeliveryOptionsProps = {
    /**
     * Icon-only trigger (the compose dialog's compact layout puts it at the end
     * of the "will be sent on" strip). The label moves to the tooltip/aria; when
     * set, a small × badge in the corner clears it.
     */
    iconOnly?: boolean;
};

const MessageDeliveryOptions = ({ iconOnly = false }: MessageDeliveryOptionsProps = {}) => {
    const { t } = useTranslation(namespace);
    const dispatch = useAppDispatch();
    const [open, setOpen] = useState(false);

    // Read state directly from Redux
    const retryLimitDuration = useAppSelector((state: CommonRootState) => state.ScheduledMessageEditorReducer.retryLimitDuration);
    const retryLimitUnit = useAppSelector((state: CommonRootState) => state.ScheduledMessageEditorReducer.retryLimitUnit);

    // Check if delivery options are configured (non-default)
    const hasCustomDeliverySettings = !!retryLimitDuration ||
        (retryLimitUnit && retryLimitUnit !== DefaultRetryLimitUnit);

    // Handler for Retry Limit Unit
    const handleRetryLimitUnitChange = (value: string) => {
        const newUnit = value as RetryLimitUnit;
        let newDuration = retryLimitDuration;

        // If a unit is selected and the current duration is not set (is undefined)
        if (newUnit && (retryLimitDuration === undefined)) {
            newDuration = MessageOvedueLimit; // Default duration
        }

        dispatch(setRetryLimitUnit(newUnit));
        if (newDuration !== retryLimitDuration) {
            dispatch(setRetryLimitDuration(newDuration));
        }
    };

    // Handler for Retry Limit Duration
    const handleRetryLimitDurationChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        const newDuration = normalizeRetryDuration(value);
        let newUnit = retryLimitUnit;

        // If a duration value is entered (not undefined) and the current unit is not set
        if (newDuration !== undefined && !retryLimitUnit) {
            newUnit = DefaultRetryLimitUnit; // Default unit
        }

        dispatch(setRetryLimitDuration(newDuration));
        if (newUnit !== retryLimitUnit) {
            dispatch(setRetryLimitUnit(newUnit));
        }
    };

    const handleClose = () => {
        setOpen(false);
    };

    const clearDeliverySettings = () => {
        dispatch(setRetryLimitDuration(undefined));
        dispatch(setRetryLimitUnit(undefined));
    };

    const getButtonText = () => {
        if (hasCustomDeliverySettings) {
            const w = effectiveRetryWindow(retryLimitDuration, retryLimitUnit ?? DefaultRetryLimitUnit, MessageOvedueLimit);
            const unitText = w.unit === 'hours' ? t('messageDelivery.hours') : t('messageDelivery.minutes');
            return `${t('messageDelivery.retry')} ${w.value} ${unitText}`;
        }
        return t('messageScheduling.setDeliveryOptions');
    };

    return (
        <div className={iconOnly ? "flex items-center" : "flex items-center justify-center h-12 min-h-12"}>
            <div className="relative w-full">
                <Tooltip>
                    <TooltipTrigger asChild>
                        <Popover open={open} onOpenChange={setOpen}>
                            <PopoverTrigger asChild>
                                <Button
                                    variant={hasCustomDeliverySettings ? "default" : iconOnly ? "ghost" : "outline"}
                                    aria-label={getButtonText()}
                                    className={iconOnly
                                        ? cn(
                                            "h-8 w-8 min-h-8 min-w-0 p-0 rounded-full border transition-all duration-200",
                                            hasCustomDeliverySettings
                                                ? "border-[var(--cmp-brand)] bg-[var(--cmp-brand-soft)] text-[var(--cmp-brand-ink)]"
                                                : "border-transparent bg-transparent text-[var(--tasks-text-secondary)] hover:bg-[var(--color-surface-muted)]",
                                        )
                                        : cn(
                                        "text-sm font-medium rounded-[20px] px-4 py-1.5 h-8 min-h-8",
                                        "border",
                                        "w-full min-w-[110px] sm:min-w-[140px] max-w-[200px]",
                                        "overflow-hidden whitespace-nowrap",
                                        "transition-all duration-200",
                                        // Same treatment as the recurrence pill: a tinted pill with a
                                        // brand hairline and brand INK, not a solid primary fill with
                                        // a drop shadow. Colour stays a small saturated mark.
                                        hasCustomDeliverySettings
                                            ? "border-[var(--cmp-brand)] bg-[var(--cmp-brand-soft)] text-[var(--cmp-brand-ink)]"
                                            : "border-[var(--color-border)] bg-transparent text-[var(--tasks-text-secondary)]",
                                        "hover:border-[var(--color-accent)]"
                                    )}
                                >
                                    {!iconOnly && hasCustomDeliverySettings && (
                                        <Tooltip>
                                            <TooltipTrigger asChild>
                                                <span
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        clearDeliverySettings();
                                                    }}
                                                    /* `me-2`, not `mr-2`: a physical margin-right does
                                                       not mirror in Hebrew/RTL. */
                                                    className="flex items-center justify-center w-5 h-5 rounded-full cursor-pointer transition-all duration-300 me-2 hover:opacity-60"
                                                >
                                                    <X className="h-3.5 w-3.5 text-[var(--cmp-brand-ink)] transition-colors duration-300" />
                                                </span>
                                            </TooltipTrigger>
                                            <TooltipContent>
                                                <p>{t('messageDelivery.clearSettings')}</p>
                                            </TooltipContent>
                                        </Tooltip>
                                    )}
                                    {!iconOnly && getButtonText()}
                                    {/* No margin class: the button is flex with `gap-2` already, and gap is
                                        direction-agnostic. (The second half of this comment used to say
                                        `ms-*` compiles to a physical margin-left in this build — it does
                                        not. Compiling through this repo's own @tailwindcss/postcss v4.1
                                        emits `margin-inline-start`. Verified, not assumed.) */}
                                    <Settings className="h-[18px] w-[18px] transition-all duration-200" />
                                </Button>
                            </PopoverTrigger>
                            <PopoverContent
                                align="start"
                                side="top"
                                className="w-[360px] rounded-xl border border-[var(--tasks-cell-border)] p-4 shadow-[0_10px_30px_rgba(20,37,63,0.15)]"
                                zIndex={91000}
                            >
                                <div className="flex flex-col gap-3">
                                    <div className="text-base font-semibold text-[var(--color-text)]">
                                        {t('messageDelivery.title')}
                                    </div>
                                    <div className="text-sm text-[var(--tasks-text-secondary)] whitespace-nowrap">
                                        {t('messageDelivery.retryDescription')}
                                    </div>
                                    <div className="flex flex-col gap-2">
                                        <div className="flex flex-row items-center gap-2">
                                            <Input
                                                type="number"
                                                min={1}
                                                value={retryLimitDuration || ''}
                                                onChange={handleRetryLimitDurationChange}
                                                placeholder={`${MessageOvedueLimit}`}
                                                className="h-10 w-20 rounded-lg px-2 text-center text-sm"
                                            />
                                            <Select
                                                value={retryLimitUnit ?? DefaultRetryLimitUnit}
                                                onValueChange={handleRetryLimitUnitChange}
                                            >
                                                <SelectTrigger className="h-10 min-w-[120px] rounded-lg px-3 text-start text-sm">
                                                    <SelectValue />
                                                </SelectTrigger>
                                                <SelectContent zIndex={92000}>
                                                    <SelectItem value="minutes">{t('messageDelivery.minutes')}</SelectItem>
                                                    <SelectItem value="hours">{t('messageDelivery.hours')}</SelectItem>
                                                </SelectContent>
                                            </Select>
                                        </div>
                                    </div>
                                </div>
                            </PopoverContent>
                        </Popover>
                    </TooltipTrigger>
                    <TooltipContent>
                        <p>{t('messageDelivery.configureRetry')}</p>
                    </TooltipContent>
                </Tooltip>
                {iconOnly && hasCustomDeliverySettings && (
                    <button
                        type="button"
                        onClick={clearDeliverySettings}
                        aria-label={t('messageDelivery.clearSettings')}
                        className="absolute -top-1 -end-1 grid h-4 w-4 place-items-center rounded-full bg-[var(--cmp-brand-ink)] text-white hover:opacity-80"
                    >
                        <X className="h-2.5 w-2.5" />
                    </button>
                )}
            </div>
        </div>
    )
}

export default memo(MessageDeliveryOptions);